/* eslint-disable no-unused-vars, no-undef, no-new, camelcase */
class StoreSelectorButton extends HTMLElement {
  constructor () {
    super()
    this.button = this.querySelector('button')
    this.list = this.querySelector('.store-selector__list')
    this.links = this.querySelectorAll('a[data-tracking]')

    if (!this.button || !this.list) return

    this.button.addEventListener('click', () => {
      this.trackEvent('store-selector-trigger', 'click')
      const isOpen = this.button.getAttribute('aria-expanded') === 'true'
      if (isOpen) {
        this.hideDropdown()
      } else {
        this.showDropdown()
      }
    })

    document.addEventListener('click', (ev) => {
      const isInside = this.contains(ev.target)
      if (!isInside && this.button.getAttribute('aria-expanded') === 'true') {
        this.hideDropdown()
      }
    })

    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.button.getAttribute('aria-expanded') === 'true') {
        this.hideDropdown()
      }
    })

    // Add click tracking for store options
    this.links.forEach(link => {
      link.addEventListener('click', () => {
        const trackingId = link.getAttribute('data-tracking')
        this.trackEvent(trackingId, 'select')
      })
    })
  }

  trackEvent (trackingId, action) {
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'StoreSelector', {
        action,
        label: trackingId,
      })
    }

    // Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'store_selector', {
        event_category: 'Store Selection',
        event_action: action,
        event_label: trackingId,
      })
    }

    // TikTok Pixel
    if (typeof ttq !== 'undefined') {
      ttq.track('StoreSelector', {
        action,
        label: trackingId,
      })
    }
  }

  showDropdown () {
    this.list.classList.remove('hidden')
    this.button.setAttribute('aria-expanded', 'true')
  }

  hideDropdown () {
    this.list.classList.add('hidden')
    this.button.setAttribute('aria-expanded', 'false')
  }
}

/**
 * Helper to run code once the document is fully loaded.
 */
function runWhenReady (callback) {
  if (document.readyState === 'complete') {
    callback()
  } else {
    document.addEventListener('readystatechange', () => {
      if (document.readyState === 'complete') callback()
    })
  }
}

class StoreSelector {
  constructor () {
    try {
      // Skip on dev theme
      if (document.documentElement.classList.contains('dev-theme')) {
        return
      }

      this.config = window.Tentbox.storeSelector
      this.translations = this.config.translations

      // Define store mappings with enabled checks.
      try {
        this.storeMappings = {
          US: {
            domain: new URL(this.config.stores.us.url).hostname,
            path: new URL(this.config.stores.us.url).pathname,
            enabled: this.config.stores.us.enabled,
            force_banner: this.config.stores.us.force_banner,
            flag: {
              square: this.config.flags.us_circle,
            },
          },
          UK: {
            domain: new URL(this.config.stores.uk.url).hostname,
            path: new URL(this.config.stores.uk.url).pathname,
            enabled: true,
            force_banner: this.config.stores.uk.force_banner,
            flag: {
              square: this.config.flags.gb_circle,
            },
          },
          FR: {
            domain: new URL(this.config.stores.fr.url).hostname,
            path: '/fr-fr',
            enabled: this.config.stores.fr.enabled,
            force_banner: this.config.stores.fr.force_banner,
            flag: {
              square: this.config.flags.fr_circle,
            },
          },
          DE: {
            domain: new URL(this.config.stores.de.url).hostname,
            path: '/de-de',
            enabled: this.config.stores.de.enabled,
            force_banner: this.config.stores.de.force_banner,
            flag: {
              square: this.config.flags.de_circle,
            },
          },
          IT: {
            domain: new URL(this.config.stores.it.url).hostname,
            path: '/it-it',
            enabled: this.config.stores.it.enabled,
            force_banner: this.config.stores.it.force_banner,
            flag: {
              square: this.config.flags.it_circle,
            },
          },
          EU: {
            domain: new URL(this.config.stores.eu.url).hostname,
            path: new URL(this.config.stores.eu.url).pathname,
            enabled: this.config.stores.eu.enabled,
            force_banner: this.config.stores.eu.force_banner,
            flag: {
              square: this.config.flags.eu_circle,
            },
          },
          IE: {
            domain: new URL(this.config.stores.ie.url).hostname,
            path: new URL(this.config.stores.ie.url).pathname,
            enabled: this.config.stores.ie.enabled,
            force_banner: this.config.stores.ie.force_banner,
            flag: {
              square: this.config.flags.ie_circle,
            },
          },
          GR: {
            domain: new URL(this.config.stores.gr.url).hostname,
            path: new URL(this.config.stores.gr.url).pathname,
            enabled: this.config.stores.gr.enabled,
            force_banner: this.config.stores.gr.force_banner,
            flag: {
              square: this.config.flags.gr_circle,
            },
          },
          CY: {
            domain: new URL(this.config.stores.cy.url).hostname,
            path: new URL(this.config.stores.cy.url).pathname,
            enabled: this.config.stores.cy.enabled,
            force_banner: this.config.stores.cy.force_banner,
            flag: {
              square: this.config.flags.cy_circle,
            },
          },
        }
      } catch (e) {
        return
      }

      this.currentDomain = new URL(window.location.href).hostname.replace('www.', '')

      this.triggerBtns = document.querySelectorAll('.store-icon-trigger')
      this.flagContainers = document.querySelectorAll('.store-icon-flag')

      if (!this.triggerBtns.length || !this.flagContainers.length) {
        return
      }

      this.updateFlagIcon()
      this.triggerBtns.forEach((btn) => {
        btn.removeAttribute('disabled')
      })

      if (Shopify?.designMode) {
        return
      }

      const stayChoice = localStorage.getItem('tentboxStayChoice')
      const userStayed = stayChoice ? JSON.parse(stayChoice).choice === 'true' : false

      // Bot detection: check the user agent for bot keywords.
      const isBot = /bot|crawl|spider/i.test(navigator.userAgent)

      // Skip IP checks for bots entirely
      if (isBot) {
        this.country = 'GB'
        runWhenReady(() => this.init(!userStayed))
        return
      }

      // Check for cached IP info in session or local storage
      try {
        // First check sessionStorage (current session)
        const sessionIpInfo = this.getSessionIpInfo()
        if (sessionIpInfo) {
          this.country = sessionIpInfo.country
          runWhenReady(() => this.init(!userStayed))
          return
        }

        // Then check localStorage (persistent)
        const cachedIpInfo = this.getCachedIpInfo()
        if (cachedIpInfo) {
          // Handle both old ipinfo.io (country) and new ipapi.com (country_code) formats
          this.country = cachedIpInfo.country_code || cachedIpInfo.country

          // Also update sessionStorage for future page views in this session
          this.updateSessionIpInfo(cachedIpInfo)
          runWhenReady(() => this.init(!userStayed))
          return
        }

        // Use the access key from theme settings if available, otherwise use placeholder
        const accessKey = window.Tentbox.storeSelector.ipapi_access_key

        if (!accessKey) {
          this.country = 'GB'
          runWhenReady(() => this.init(!userStayed))
          return
        }

        fetch(`https://api.ipapi.com/api/check?access_key=${accessKey}`)
          .then(response => {
            return response.json()
          })
          .then(data => {
            if (data.success === false) {
              this.country = 'GB'
              runWhenReady(() => this.init(!userStayed))
              return
            }

            this.country = data.country_code

            // Update both storage types if available
            this.cacheIpInfo(data)
            this.updateSessionIpInfo(data)
            runWhenReady(() => this.init(!userStayed))
          })
          .catch(() => {
            this.country = 'GB'
            runWhenReady(() => this.init(!userStayed))
          })
      } catch (e) {
        // Fallback if any error occurs in the caching logic
        this.country = 'GB'
        runWhenReady(() => this.init(!userStayed))
      }
    } catch (mainError) {
      // Silently fail if there's a critical error
    }
  }

  getCachedIpInfo () {
    const cached = localStorage.getItem('tentboxIpInfo')
    if (!cached) return null

    const { data, timestamp } = JSON.parse(cached)
    const twoWeeks = 14 * 24 * 60 * 60 * 1000 // Extended from one week to two weeks

    // Invalidate if older than 2 weeks
    if (Date.now() - timestamp > twoWeeks) {
      localStorage.removeItem('tentboxIpInfo')
      return null
    }

    // Ensure country property maps to country_code for backward compatibility
    if (!data.country_code && data.country) {
      data.country_code = data.country
    }

    return data
  }

  getSessionIpInfo () {
    try {
      const cached = sessionStorage.getItem('tentboxSessionIpInfo')
      if (!cached) return null
      return JSON.parse(cached)
    } catch (e) {
      return null
    }
  }

  updateSessionIpInfo (data) {
    try {
      if (!data || !data.country_code) {
        return
      }
      sessionStorage.setItem('tentboxSessionIpInfo', JSON.stringify(data))
    } catch (e) {
      // Silently fail if session storage is not available
    }
  }

  shouldShowBanner () {
    // Get the detected store key based on user's location
    const detectedStoreKey = this.getDetectedStoreKey()

    // If the detected store has force_banner enabled, check forced banner preferences
    if (detectedStoreKey && this.storeMappings[detectedStoreKey] && this.storeMappings[detectedStoreKey].force_banner) {
      // Check if we have a forced banner preference for this specific store
      const forcedChoice = localStorage.getItem('tentboxStayChoiceForced')

      if (forcedChoice) {
        const forcedData = JSON.parse(forcedChoice)

        // Only respect the choice if it's for the same store
        if (forcedData.store !== detectedStoreKey) {
          return true
        }

        // Check if the forced choice has expired (shorter expiry - 1 day)
        const oneDay = 24 * 60 * 60 * 1000
        if (Date.now() - forcedData.timestamp > oneDay) {
          localStorage.removeItem('tentboxStayChoiceForced')
          return true
        }

        return false
      }

      // No forced choice found, show the banner
      return true
    }

    // Otherwise, check based on the existing time-based rules for regular stores
    const stayChoice = localStorage.getItem('tentboxStayChoice')

    if (!stayChoice) return true

    const { timestamp } = JSON.parse(stayChoice)
    const threeDays = 3 * 24 * 60 * 60 * 1000

    // Show banner if stay choice is older than 3 days
    return Date.now() - timestamp > threeDays
  }

  cacheIpInfo (data) {
    // Skip caching if no country data is available
    if (!data || !data.country_code) {
      return
    }

    try {
      const cache = {
        data,
        timestamp: Date.now(),
      }
      localStorage.setItem('tentboxIpInfo', JSON.stringify(cache))
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }

  init (shouldCheckMismatch) {
    this.updateFlagIcon()
    this.triggerBtns.forEach((btn) => {
      btn.removeAttribute('disabled')
    })

    // Get the detected store key based on user's location
    const detectedStoreKey = this.getDetectedStoreKey()
    const currentStoreKey = this.getCurrentStoreKey()

    // Check if the detected store has force_banner enabled
    const forceBanner = detectedStoreKey &&
                        this.storeMappings[detectedStoreKey] &&
                        this.storeMappings[detectedStoreKey].force_banner

    // Always check if we need to force the banner regardless of the user's choice
    if (forceBanner && detectedStoreKey !== currentStoreKey) {
      this.openBanner()
      return
    }

    // Only check for regular domain mismatch if shouldCheckMismatch is true
    if (shouldCheckMismatch) {
      const mismatch = this.isDomainMismatch(this.country, this.currentDomain)
      const shouldShow = this.shouldShowBanner()

      if (mismatch && shouldShow) {
        this.openBanner()
      }
    }
  }

  isDomainMismatch (countryCode, currentDomain) {
    const detectedStore = this.getDetectedStoreKey()
    const currentStore = this.getCurrentStoreKey()
    return detectedStore !== currentStore
  }

  decodeFlag (encodedSvg) {
    return encodedSvg
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
  }

  updateFlagIcon () {
    const currentStoreKey = this.getCurrentStoreKey()
    const currentStore = this.storeMappings[currentStoreKey]

    if (!currentStore) return

    this.flagContainers.forEach((container) => {
      container.innerHTML = this.decodeFlag(currentStore.flag.square)
    })
  }

  getCurrentStoreKey () {
    const shopifyDomain = window.Shopify?.shop || ''
    const path = window.location.pathname

    // Check Shopify domains first
    if (shopifyDomain === 'tentbox-us.myshopify.com') return 'US'
    if (shopifyDomain === 'tentbox-6623.myshopify.com') return 'UK'
    if (shopifyDomain === 'tentbox-eu1.myshopify.com') {
      if (path.startsWith('/fr-fr')) return 'FR'
      if (path.startsWith('/de-de')) return 'DE'
      if (path.startsWith('/it-it')) return 'IT'
      return 'EU'
    }

    // Check custom domains
    if (this.currentDomain.includes('us.tentbox.com')) return 'US'
    if (this.currentDomain.includes('eu.tentbox.com')) {
      if (path.startsWith('/fr-fr')) return 'FR'
      if (path.startsWith('/de-de')) return 'DE'
      if (path.startsWith('/it-it')) return 'IT'
      return 'EU'
    }
    return 'UK'
  }

  getDetectedStoreKey () {
    // First check if we have a stored previous choice
    const previousStore = localStorage.getItem('tentboxPreviousStore')
    if (previousStore) {
      return previousStore
    }

    const country = this.country?.toUpperCase()

    // If no country detected, return current store
    if (!country) {
      return this.getCurrentStoreKey()
    }

    // Check specific country stores first, but only if they're enabled
    if (country === 'US' && this.storeMappings.US.enabled) {
      return 'US'
    }
    if (country === 'GB' || country === 'UK') {
      return 'UK'
    }
    if (country === 'FR' && this.storeMappings.FR.enabled) {
      return 'FR'
    }
    if (country === 'DE' && this.storeMappings.DE.enabled) {
      return 'DE'
    }
    if (country === 'IT' && this.storeMappings.IT.enabled) {
      return 'IT'
    }
    if (country === 'IE' && this.storeMappings.IE.enabled) {
      return 'IE'
    }
    if (country === 'GR' && this.storeMappings.GR.enabled) {
      return 'GR'
    }
    if (country === 'CY' && this.storeMappings.CY.enabled) {
      return 'CY'
    }

    // If country is in EU and specific country store is not enabled, fallback to EU store
    const euCountries = ['FR', 'DE', 'IT', 'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'GR', 'HU', 'IE', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE']
    if (euCountries.includes(country) && this.storeMappings.EU.enabled) {
      return 'EU'
    }

    // If no matching store found, return current store
    return this.getCurrentStoreKey()
  }

  replaceShortcodes (text, storeKey, makeBold = false) {
    if (!text) return ''

    const store = this.getStoreInfo(storeKey)

    // Get user's actual location based on IP
    const userLocationKey = this.country?.toUpperCase()
    let userLocation = null

    if (userLocationKey) {
      // Map the country code to a store key if available
      if (userLocationKey === 'GB' || userLocationKey === 'UK') {
        userLocation = this.getStoreInfo('UK')
      } else if (userLocationKey === 'US' && this.storeMappings.US.enabled) {
        userLocation = this.getStoreInfo('US')
      } else if (userLocationKey === 'FR' && this.storeMappings.FR.enabled) {
        userLocation = this.getStoreInfo('FR')
      } else if (userLocationKey === 'DE' && this.storeMappings.DE.enabled) {
        userLocation = this.getStoreInfo('DE')
      } else if (userLocationKey === 'IT' && this.storeMappings.IT.enabled) {
        userLocation = this.getStoreInfo('IT')
      } else if (userLocationKey === 'IE' && this.storeMappings.IE.enabled) {
        userLocation = this.getStoreInfo('IE')
      } else if (userLocationKey === 'GR' && this.storeMappings.GR.enabled) {
        userLocation = this.getStoreInfo('GR')
      } else if (userLocationKey === 'CY' && this.storeMappings.CY.enabled) {
        userLocation = this.getStoreInfo('CY')
      } else if (this.isEuCountry(userLocationKey) && this.storeMappings.EU.enabled) {
        userLocation = this.getStoreInfo('EU')
      } else {
        // Default to current store if no mapping
        userLocation = store
      }
    } else {
      // Fallback to current store if no country info available
      userLocation = store
    }

    const patterns = {
      region: {
        base: store.label,
        prefix: store.prefix || store.label,
        code: store.code || storeKey,
      },
      current_store: {
        base: this.getStoreInfo(this.getCurrentStoreKey()).label,
        prefix: this.getStoreInfo(this.getCurrentStoreKey()).prefix || this.getStoreInfo(this.getCurrentStoreKey()).label,
        code: this.getStoreInfo(this.getCurrentStoreKey()).code || this.getCurrentStoreKey(),
      },
      suggested_store: {
        base: this.getStoreInfo(this.getDetectedStoreKey()).label,
        prefix: this.getStoreInfo(this.getDetectedStoreKey()).prefix || this.getStoreInfo(this.getDetectedStoreKey()).label,
        code: this.getStoreInfo(this.getDetectedStoreKey()).code || this.getDetectedStoreKey(),
      },
      user_location: {
        base: userLocation.label,
        prefix: userLocation.prefix || userLocation.label,
        code: userLocation.code || userLocationKey,
      },
    }

    return text.replace(/\{\{\s*([a-z_]+)(?:\s*\|\s*([a-z]+))?\s*\}\}/g, (match, key, modifier) => {
      if (!patterns[key]) return match
      const value = modifier ? patterns[key][modifier] || patterns[key].base : patterns[key].base
      return makeBold ? `<span class="font-semibold">${value}</span>` : value
    })
  }

  // Helper method to check if a country code is an EU country
  isEuCountry (countryCode) {
    const euCountries = ['FR', 'DE', 'IT', 'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'GR', 'HU', 'IE', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE']
    return euCountries.includes(countryCode)
  }

  getStoreInfo (storeKey) {
    const storeKey_lower = storeKey.toLowerCase()
    let translationKey = storeKey_lower

    // Handle special cases
    if (storeKey_lower === 'gr') translationKey = 'gr'
    else if (storeKey_lower === 'cy') translationKey = 'cy'

    const storeTranslations = this.translations.storeNames[translationKey]

    return {
      flagIso: storeKey_lower,
      label: storeTranslations.name,
      code: storeTranslations.code,
      prefix: storeTranslations.prefix,
      ...this.storeMappings[storeKey],
    }
  }

  openBanner () {
    if (this.isCurrentPathWelcomePage()) {
      this.openModal()
      return
    }

    // Wait for translations to be available
    if (!this.translations?.banner) {
      setTimeout(() => this.openBanner(), 100)
      return
    }

    if (this.bannerEl) {
      this.bannerEl.classList.remove('hidden')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.bannerEl.classList.remove('-translate-y-full')
        })
      })
      return
    }

    const banner = document.createElement('div')
    banner.classList.add(
      'store-selector__banner',
      'fixed',
      'inset-x-0',
      'top-0',
      'bg-black',
      'text-white',
      'z-[9999]',
      'transition-transform',
      'duration-500',
      '-translate-y-full',
    )

    const container = document.createElement('div')
    container.classList.add(
      'store-selector__banner-content',
      'max-w-4xl',
      'mx-auto',
      'px-4',
      'py-6',
    )
    banner.appendChild(container)

    const flexContainer = document.createElement('div')
    flexContainer.classList.add(
      'flex',
      'flex-col',
      'lg:flex-row',
      'items-center',
      'justify-center',
      'gap-1',
      'lg:gap-4',
      'text-center',
    )
    container.appendChild(flexContainer)

    const currentText = document.createElement('div')
    currentText.innerHTML = this.getCurrentStoreText()
    currentText.classList.add('lg:whitespace-nowrap', 'text-lg', 'mb-2', 'lg:mb-0')
    flexContainer.appendChild(currentText)

    const goBtn = this.createStoreButton(this.getStoreInfo(this.getDetectedStoreKey()), false)
    goBtn.addEventListener('click', () => {
      localStorage.setItem('tentboxPreviousStore', this.getDetectedStoreKey())
      const detectedStore = this.getStoreInfo(this.getDetectedStoreKey())
      // For normal pages, just go to the homepage
      const finalUrl = `https://${detectedStore.domain}${detectedStore.path || ''}`
      window.location.href = finalUrl
    })
    flexContainer.appendChild(goBtn)

    const altText = document.createElement('div')
    const alternativeText = this.translations.banner.alternativeStoreText || ''
    altText.innerHTML = alternativeText
    altText.classList.add('lg:whitespace-nowrap')
    flexContainer.appendChild(altText)

    // Determine if this is a forced banner based on the detected store
    const detectedStoreKey = this.getDetectedStoreKey()
    const isForced = detectedStoreKey &&
                    this.storeMappings[detectedStoreKey] &&
                    this.storeMappings[detectedStoreKey].force_banner

    const stayBtn = this.createStoreButton(this.getStoreInfo(this.getCurrentStoreKey()), true)
    stayBtn.addEventListener('click', () => {
      // For forced banners, we store a separate preference with a shorter expiry
      if (isForced) {
        localStorage.setItem('tentboxStayChoiceForced', JSON.stringify({
          choice: 'true',
          store: detectedStoreKey,
          timestamp: Date.now(),
        }))
      } else {
        // Regular stay choice with longer expiry
        localStorage.setItem('tentboxStayChoice', JSON.stringify({
          choice: 'true',
          timestamp: Date.now(),
        }))
      }
      this.hideBanner()
    })
    flexContainer.appendChild(stayBtn)

    document.body.appendChild(banner)
    this.bannerEl = banner

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        banner.classList.remove('-translate-y-full')
      })
    })
  }

  openModal () {
    window.dispatchEvent(
      new CustomEvent('open:modal', {
        detail: {
          width: 'min(600px, 100svw)',
          modalClassRemove: ['p-4', 'md:p-4'],
          closeClassAdd: ['translate-y-[5px]'],
          headerClassAdd: ['pr-3', 'pt-2'],
          headerClassRemove: ['mb-4', 'border-b', 'border-steel', 'pb-2'],
          bodyClassAdd: ['px-8', 'md:px-8', 'pt-4', 'md:pt-4', 'pb-6', 'md:pb-6'],
        },
      }),
    )

    // Use a timeout safeguard to avoid an endless loop waiting for the modal.
    const checkModalReady = (() => {
      const startTime = Date.now()
      return () => {
        const modal = document.querySelector('.reusable-modal')
        const modalTitle = modal?.querySelector('.reusable-modal__title')
        const modalBody = modal?.querySelector('.reusable-modal__body')

        if (!modalTitle || !modalBody) {
          if (Date.now() - startTime < 5000) {
            requestAnimationFrame(checkModalReady)
          }
          return
        }

        modalTitle.innerHTML = ''
        modalBody.innerHTML = `
          <div class="flex flex-col items-center gap-4 text-center">
            <div class="text-xl font-bold text-black">
              ${this.replaceShortcodes(this.translations.modal.title, this.getCurrentStoreKey())}
            </div>
            <div class="text-black max-w-[324px] mx-auto">
              ${this.replaceShortcodes(this.translations.modal.body, this.getCurrentStoreKey(), true)}
            </div>
            <div class="grid grid-cols-1 gap-1 items-center">
              <button class="stay-button button button-outline w-full md:w-fit justify-self-center flex items-center gap-2 rounded py-3 px-6 whitespace-nowrap cursor-pointer select-none border">
                <div class="w-6 h-6 rounded-full overflow-hidden">
                  ${this.decodeFlag(this.getStoreInfo(this.getCurrentStoreKey()).flag.square)}
                </div>
                <span class="text-lg">${this.replaceShortcodes(this.translations.modal.stayButtonText, this.getCurrentStoreKey())}</span>
              </button>
              <div class="text-lg text-black">${this.translations.banner.alternativeStoreText}</div>
              <button class="go-button button button-outline w-full md:w-fit justify-self-start flex items-center gap-2 rounded py-3 px-6 whitespace-nowrap cursor-pointer select-none border mb-6">
                <div class="w-6 h-6 rounded-full overflow-hidden">
                  ${this.decodeFlag(this.getStoreInfo(this.getDetectedStoreKey()).flag.square)}
                </div>
                <span class="text-lg">${this.replaceShortcodes(this.translations.modal.goButtonText, this.getDetectedStoreKey())}</span>
              </button>
            </div>
          </div>
        `

        const stayButton = modalBody.querySelector('.stay-button')
        const goButton = modalBody.querySelector('.go-button')

        stayButton.addEventListener('click', () => {
          localStorage.setItem('tentboxStayChoice', JSON.stringify({
            choice: 'true',
            timestamp: Date.now(),
          }))
          window.dispatchEvent(new CustomEvent('close:modal'))
        })

        goButton.addEventListener('click', () => {
          localStorage.setItem('tentboxPreviousStore', this.getDetectedStoreKey())
          const detectedStore = this.getStoreInfo(this.getDetectedStoreKey())

          // For welcome pages, include the current path
          if (this.isCurrentPathWelcomePage()) {
            const welcomePathForStore = this.getWelcomePathForStore(detectedStore)
            const finalUrl = `https://${detectedStore.domain}${detectedStore.path || ''}/${welcomePathForStore}`
            window.location.href = finalUrl
          } else {
            // For regular pages, just go to homepage
            const finalUrl = `https://${detectedStore.domain}${detectedStore.path || ''}`
            window.location.href = finalUrl
          }
        })
      }
    })()

    checkModalReady()
  }

  // Helper function to get the welcome page path for a specific store
  getWelcomePathForStore (store) {
    // Check if we have welcome page paths defined in the config
    if (this.config.welcomePaths && store) {
      const storeKey = Object.keys(this.storeMappings).find(key =>
        this.storeMappings[key].domain === store.domain &&
        this.storeMappings[key].path === store.path,
      )

      if (storeKey && this.config.welcomePaths[storeKey.toLowerCase()]) {
        return `pages/${this.config.welcomePaths[storeKey.toLowerCase()]}`
      }
    }

    // If no specific path is found or config is missing, use the English default
    return 'pages/welcome'
  }

  // Helper function to check if the current page is a welcome page
  isCurrentPathWelcomePage () {
    // Check if the welcome page flag is already determined
    if (typeof this.config.isWelcomePage !== 'undefined') {
      return this.config.isWelcomePage
    }

    // Otherwise check if the current path matches any welcome path pattern using the configured welcome paths
    const currentPath = window.location.pathname.toLowerCase()
    if (currentPath.includes('/pages/')) {
      return Object.values(this.config.welcomePaths || {}).some(slug =>
        currentPath.includes(`/pages/${slug.toLowerCase()}`),
      )
    }

    return false
  }

  createStoreButton (store, isStayButton) {
    const btn = document.createElement('button')
    btn.classList.add(
      'flex',
      'items-center',
      'gap-2',
      'rounded',
      'py-1',
      'px-3',
      'whitespace-nowrap',
      'cursor-pointer',
      'select-none',
      'border',
      'border-white',
      'bg-white',
      'text-black',
      'hover:bg-white-h',
    )

    const buttonText = isStayButton
      ? this.replaceShortcodes(this.translations.banner.stayButtonText, this.getCurrentStoreKey())
      : this.replaceShortcodes(this.translations.banner.goButtonText, this.getDetectedStoreKey())

    btn.innerHTML = `
      <div class="w-6 h-6 rounded-full overflow-hidden">
        ${this.decodeFlag(store.flag.square)}
      </div>
      <span>${buttonText}</span>
    `
    return btn
  }

  getCurrentStoreText () {
    return this.replaceShortcodes(
      this.translations.banner.chooseOption,
      this.getDetectedStoreKey(),
      true, // Make the store name bold
    )
  }

  hideBanner () {
    if (!this.bannerEl) return
    this.bannerEl.classList.add('-translate-y-full')
    setTimeout(() => {
      this.bannerEl.classList.add('hidden')
    }, 500)
  }

  testWithCountry (countryCode) {
    this.country = countryCode

    // Clear any existing choices
    localStorage.removeItem('tentboxStayChoice')
    localStorage.removeItem('tentboxPreviousStore')

    // Initialize with forced check
    this.init(true)
  }
}

// Define the custom element
customElements.define('store-selector-button', StoreSelectorButton)

// Store the instance globally for debugging
window.tbStoreSelector = new StoreSelector()
